<template>
	<div class="font-sans">
		<!-- ── Top bar ── -->
		<div class="flex items-center justify-end mb-5">
			<router-link
				to="/admin/user"
				class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200">
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2.5"
						d="M12 4v16m8-8H4" />
				</svg>
				Create User
			</router-link>
		</div>

		<!-- ── Table ── -->
		<div
			v-if="
				(usersData.length > 0 && userInfo.can_view_users) ||
				userInfo.is_system_admin
			"
			class="overflow-x-auto rounded-xl border border-primary-100 dark:border-dark-600">
			<table class="w-full border-separate border-spacing-0">
				<thead>
					<tr>
						<th
							v-for="h in ['User Name', 'Email', 'Phone Number', 'Created At']"
							:key="h"
							class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
							{{ h }}
						</th>
						<th
							class="text-center text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-100 dark:bg-dark-500 border-b border-primary-100 dark:border-dark-500">
							Action
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="user in usersData"
						:key="user.ID"
						class="hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors duration-150">
						<td
							class="px-4 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ user.Name }}
						</td>
						<td
							class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ user.Email }}
						</td>
						<td
							class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ user.Phone }}
						</td>
						<td
							class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ $formatDate(user.CreatedAt) }}
						</td>
						<td
							class="px-4 py-3.5 text-center border-b border-primary-50 dark:border-dark-600">
							<span
								v-if="user.is_current_user"
								class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300">
								<span class="w-1.5 h-1.5 rounded-full bg-primary-400" />
								You
							</span>
							<button
								v-else
								@click="!user.delete ?  disableUser(user) : enableUser(user)"
								:disabled="loadingUserId === user.ID"
								class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
								:class="
									!user.delete
										? 'bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800/50 hover:bg-red-100'
										: 'bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300 border border-primary-200 dark:border-primary-400/30 hover:bg-primary-100'
								">
								<svg
									v-if="loadingUserId === user.ID"
									class="animate-spin w-3.5 h-3.5"
									fill="none"
									viewBox="0 0 24 24">
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4" />
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								{{ !user.delete ? "Deactivate" : "Activate" }}
							</button>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- ── Permission denied ── -->
		<div
			v-else-if="!userInfo.can_view_users"
			class="flex flex-col items-center justify-center py-16 gap-3 rounded-card border border-primary-100 dark:border-dark-600">
			<div
				class="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-950/30 flex items-center justify-center">
				<svg
					class="w-6 h-6 text-red-400"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
				</svg>
			</div>
			<p
				class="font-display font-bold text-[14px] text-dark-700 dark:text-white">
				Access Restricted
			</p>
			<p class="text-[13px] text-muted dark:text-muted-light">
				You do not have permission to view users.
			</p>
		</div>

		<!-- ── Empty state ── -->
		<div
			v-else
			class="flex flex-col items-center justify-center py-16 gap-3 rounded-card border border-primary-100 dark:border-dark-600">
			<div
				class="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center">
				<svg
					class="w-6 h-6 text-muted dark:text-muted-light"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
				</svg>
			</div>
			<p
				class="font-display font-bold text-[14px] text-dark-700 dark:text-white">
				No users found
			</p>
		</div>
	</div>
</template>

<script>
import { ref, watch } from "vue";
import { apiDataService } from "@/services/apiDataService";
import { showToast } from "@/constants/toast";

export default {
	name: "UserList",
	props: { users: { type: Array, default: () => [] } },
	computed: {
		userInfo() {
			return this.$store.state.auth.user || {};
		},
		forAdmin() {
			const type = Number(this.userInfo.user_type);
			return type !== 1 && type !== 2;
		},
		forClient() {
			const type = Number(this.userInfo.user_type);
			return type !== 1 && type == 2;
		},
	},
	setup(props) {
		const usersData = ref(props.users);
		const loadingUserId = ref(null);
		watch(
			() => props.users,
			(newUsers) => {
				usersData.value = newUsers;
			},
			{ immediate: true },
		);

		const disableUser = async (user) => {
			loadingUserId.value = user.ID;
			try {
				const response = await apiDataService.disableUser(user.ID, 2);
				user.delete = !user.delete;
				showToast("success", `${response.data.message}`);
			} catch (error) {
				console.error("User status update failed:", error);
			} finally {
				loadingUserId.value = null;
			}
		};
		const enableUser = async (user) => {
			loadingUserId.value = user.ID;
			try {
				const response = await apiDataService.enableUser(user.ID, 1);
				user.delete = !user.delete;
				showToast("success", `${response.data.message}`);
			} catch (error) {
				console.error("User status update failed:", error);
			} finally {
				loadingUserId.value = null;
			}
		};


		return { usersData, disableUser, enableUser, loadingUserId };
	},
};
</script>
